import { useEffect, useRef, useState } from 'react';
import './RichTextEditor.css';

/**
 * Hafif, bağımlılıksız zengin metin editörü (WYSIWYG). contentEditable + execCommand
 * üzerine kurulu; standart HTML üretir (sözleşme metinleri modalde bu HTML ile gösterilir).
 * "HTML" düğmesiyle ham kaynağa geçilebilir.
 */
export function RichTextEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const lastHtml = useRef(value);
  const [source, setSource] = useState(false);

  // Dış değer değiştiğinde (ör. sözleşme yüklenince) veya WYSIWYG'e dönünce içeriği tazele.
  useEffect(() => {
    if (source || !ref.current) return;
    if (value !== lastHtml.current || ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
      lastHtml.current = value;
    }
  }, [value, source]);

  const emit = () => {
    const html = ref.current?.innerHTML ?? '';
    lastHtml.current = html;
    onChange(html);
  };

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt('Bağlantı adresi (URL):', 'https://');
    if (url) exec('createLink', url);
  };

  const btn = (label: string, title: string, onClick: () => void, style?: React.CSSProperties) => (
    <button
      type="button"
      className="rte-btn"
      title={title}
      style={style}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <div className="rte">
      <div className="rte-toolbar">
        {btn('B', 'Kalın', () => exec('bold'), { fontWeight: 700 })}
        {btn('I', 'İtalik', () => exec('italic'), { fontStyle: 'italic' })}
        {btn('U', 'Altı çizili', () => exec('underline'), { textDecoration: 'underline' })}
        <span className="rte-sep" />
        {btn('Başlık', 'Başlık', () => exec('formatBlock', 'H2'))}
        {btn('Alt Başlık', 'Alt başlık', () => exec('formatBlock', 'H3'))}
        {btn('Paragraf', 'Normal metin', () => exec('formatBlock', 'P'))}
        <span className="rte-sep" />
        {btn('• Liste', 'Madde listesi', () => exec('insertUnorderedList'))}
        {btn('1. Liste', 'Numaralı liste', () => exec('insertOrderedList'))}
        <span className="rte-sep" />
        {btn('⯇', 'Sola hizala', () => exec('justifyLeft'))}
        {btn('≡', 'Ortala', () => exec('justifyCenter'))}
        {btn('⯈', 'Sağa hizala', () => exec('justifyRight'))}
        <span className="rte-sep" />
        {btn('🔗', 'Bağlantı ekle', addLink)}
        {btn('⌫ Temizle', 'Biçimi temizle', () => exec('removeFormat'))}
        <span className="rte-spacer" />
        <button
          type="button"
          className={`rte-btn rte-source${source ? ' active' : ''}`}
          title="HTML kaynağını düzenle"
          onClick={() => setSource((s) => !s)}
        >
          {'</>'}
        </button>
      </div>

      {source ? (
        <textarea
          className="rte-html input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
        />
      ) : (
        <div
          ref={ref}
          className="rte-area"
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
        />
      )}
    </div>
  );
}
